export type ControllerState = {
  [name: string]: AbortController;
} | null;

export interface StatusStateItem {
  name: string;
  size: number;
  progress: number;
}

export type StatusState = {
  [key: string]: StatusStateItem;
} | null;

export interface UploadState {
  count: number;
  uploading: boolean;
  controllers: ControllerState;
  status: StatusState;
  error: Record<string, Record<string, string[]>> | null;
}

export type UploadStatusPayload = StatusState | { name: string };

export interface UploadAction {
  type: string;
  count?: number;
  controllers?: ControllerState;
  status?: UploadStatusPayload;
  error?: {
    index: number;
    fields: Record<string, string[]>;
  };
}

export const uploadState: UploadState = {
  count: 0,
  uploading: false,
  controllers: null,
  status: null,
  error: null
};

export const UPLOAD_ACTIONS = Object.freeze({
  SETUP: "upload-status-setup",
  UPDATE_PROGRESS: "upload-status-update-progress",
  CANCEL_UPLOAD: "upload-status-cancel-upload",
  DECREASE: "upload-status-decrease",
  RESET: "upload-status-reset",
  ERROR: "upload-status-error"
});

export const uploadReducer = (
  state: UploadState,
  action: UploadAction
): UploadState => {
  const { type, count, controllers, status, error } = action || {};
  switch (type) {
    case UPLOAD_ACTIONS.SETUP:
      return {
        ...state,
        count: state.count + (count || 0),
        uploading: !!(state.count + (count || 0)),
        controllers: {
          ...(state.controllers || {}),
          ...(controllers || {})
        },
        status: {
          ...(state.status || {}),
          ...((status || {}) as StatusState)
        }
      };
    case UPLOAD_ACTIONS.UPDATE_PROGRESS:
      return {
        ...state,
        status: {
          ...(state.status || {}),
          ...((status || {}) as StatusState)
        }
      };
    case UPLOAD_ACTIONS.CANCEL_UPLOAD: {
      if (!status || !state.status) return state;
      const { name } = status as { name: string };
      const { [name]: _unused, ...rest } = state.status as Record<
        string,
        StatusStateItem
      >;
      return {
        ...state,
        status: {
          ...rest
        }
      };
    }
    case UPLOAD_ACTIONS.DECREASE:
      return {
        ...state,
        count: state.count - 1,
        uploading: !!(state.count - 1)
      };
    case UPLOAD_ACTIONS.RESET:
      return {
        ...state,
        error: null
      };
    case UPLOAD_ACTIONS.ERROR:
      return {
        ...state,
        error: {
          ...(state.error || {}),
          ...(error?.index !== undefined && { [error.index]: error.fields })
        }
      };
    default:
      throw new Error();
  }
};
